/**
 * Validation côté client pour les formulaires
 * RÉUTILISABILITÉ : 100% - TypeScript pur
 */

import type { PasswordStrengthDetails, PasswordStrength } from '../../application/dto/ui.types';

/**
 * Valide un email
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Valide la longueur minimale
 */
export function hasMinLength(value: string, min: number): boolean {
  return value.length >= min;
}

/**
 * Valide la longueur maximale
 */
export function hasMaxLength(value: string, max: number): boolean {
  return value.length <= max;
}

/**
 * Vérifie si contient au moins une majuscule
 */
export function hasUppercase(value: string): boolean {
  return /[A-Z]/.test(value);
}

/**
 * Vérifie si contient au moins une minuscule
 */
export function hasLowercase(value: string): boolean {
  return /[a-z]/.test(value);
}

/**
 * Vérifie si contient au moins un chiffre
 */
export function hasNumber(value: string): boolean {
  return /\d/.test(value);
}

/**
 * Vérifie si contient au moins un caractère spécial
 */
export function hasSpecialChar(value: string): boolean {
  return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);
}

/**
 * Liste des mots de passe communs à éviter
 */
const commonPasswords = [
  'password', '123456', '12345678', 'qwerty', 'abc123', 'monkey',
  'letmein', 'trustno1', 'dragon', 'baseball', 'iloveyou', 'master',
  'sunshine', 'ashley', 'bailey', 'shadow', 'superman', 'qwertyuiop',
  'welcome', 'admin', 'password123', 'azerty', 'caritas'
];

/**
 * Vérifie si le mot de passe est dans la liste des mots de passe communs
 */
export function isCommonPassword(password: string): boolean {
  return commonPasswords.includes(password.toLowerCase());
}

/**
 * Calcule la force d'un mot de passe
 */
export function calculatePasswordStrength(password: string): PasswordStrengthDetails {
  let score = 0;
  const suggestions: string[] = [];

  // Critère 1: Longueur
  const minLength = hasMinLength(password, 12);
  if (minLength) {
    score++;
  } else {
    suggestions.push('Utilisez au moins 12 caractères');
  }

  // Critère 2: Majuscules
  const uppercase = hasUppercase(password);
  if (uppercase) {
    score++;
  } else {
    suggestions.push('Ajoutez au moins une majuscule');
  }

  // Critère 3: Minuscules
  const lowercase = hasLowercase(password);
  if (lowercase) {
    score++;
  } else {
    suggestions.push('Ajoutez au moins une minuscule');
  }

  // Critère 4: Chiffres
  const number = hasNumber(password);
  if (number) {
    score++;
  } else {
    suggestions.push('Ajoutez au moins un chiffre');
  }

  // Critère 5: Caractères spéciaux
  const specialChar = hasSpecialChar(password);
  if (specialChar) {
    score++;
  } else {
    suggestions.push('Ajoutez au moins un caractère spécial');
  }

  // Pénalité: Mot de passe commun
  if (isCommonPassword(password)) {
    score = Math.max(0, score - 2);
    suggestions.push('Évitez les mots de passe communs');
  }

  // Bonus: Longueur supérieure à 16
  if (password.length >= 16) {
    score = Math.min(5, score + 1);
  }

  // Déterminer le label
  let label: PasswordStrength;
  if (score === 0) label = 'weak';
  else if (score === 1) label = 'weak';
  else if (score === 2) label = 'fair';
  else if (score === 3) label = 'good';
  else if (score === 4) label = 'strong';
  else label = 'very-strong';

  return {
    score,
    label,
    suggestions,
    hasMinLength: minLength,
    hasUppercase: uppercase,
    hasLowercase: lowercase,
    hasNumber: number,
    hasSpecialChar: specialChar,
  };
}

/**
 * Valide que deux mots de passe correspondent
 */
export function passwordsMatch(password: string, confirmation: string): boolean {
  return password === confirmation;
}

/**
 * Valide un code 2FA (6 chiffres)
 */
export function isValid2FACode(code: string): boolean {
  return /^\d{6}$/.test(code);
}

/**
 * Valide un code backup (format: XXXX-XXXX-XXXX-XXXX)
 */
export function isValidBackupCode(code: string): boolean {
  return /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(code);
}

/**
 * Valide une adresse IP v4
 */
export function isValidIPv4(ip: string): boolean {
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (!ipv4Regex.test(ip)) return false;

  const parts = ip.split('.');
  return parts.every(part => {
    const num = parseInt(part, 10);
    return num >= 0 && num <= 255;
  });
}

/**
 * Normalise un email (lowercase, trim)
 */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

/**
 * Valide un nom (au moins 2 caractères, seulement lettres et espaces)
 */
export function isValidName(name: string): boolean {
  return /^[a-zA-ZÀ-ÿ\s]{2,}$/.test(name);
}

/**
 * Valide un numéro de téléphone français
 */
export function isValidFrenchPhone(phone: string): boolean {
  // Supprime les espaces, tirets, points
  const cleaned = phone.replace(/[\s\-\.]/g, '');

  // Format: 0XXXXXXXXX (10 chiffres commençant par 0)
  // Ou format international: +33XXXXXXXXX
  return /^(0[1-9]\d{8}|(\+33|0033)[1-9]\d{8})$/.test(cleaned);
}

/**
 * Helpers pour validation de formulaires
 */
export const validators = {
  required: (value: unknown): string | null => {
    if (value === null || value === undefined) return 'Ce champ est requis';
    if (typeof value === 'string' && value.trim() === '') return 'Ce champ est requis';
    if (Array.isArray(value) && value.length === 0) return 'Ce champ est requis';
    return null;
  },

  email: (value: string): string | null => {
    if (!value) return null; // Le required s'en charge
    return isValidEmail(value) ? null : 'Email invalide';
  },

  minLength: (min: number) => (value: string): string | null => {
    if (!value) return null;
    return hasMinLength(value, min) ? null : `Minimum ${min} caractères`;
  },

  maxLength: (max: number) => (value: string): string | null => {
    if (!value) return null;
    return hasMaxLength(value, max) ? null : `Maximum ${max} caractères`;
  },

  password: (value: string): string | null => {
    if (!value) return null;
    const strength = calculatePasswordStrength(value);
    if (strength.score < 2) {
      return 'Mot de passe trop faible';
    }
    return null;
  },

  passwordMatch: (originalPassword: string) => (value: string): string | null => {
    if (!value) return null;
    return passwordsMatch(originalPassword, value) ? null : 'Les mots de passe ne correspondent pas';
  },

  code2FA: (value: string): string | null => {
    if (!value) return null;
    return isValid2FACode(value) ? null : 'Code 2FA invalide (6 chiffres)';
  },

  backupCode: (value: string): string | null => {
    if (!value) return null;
    return isValidBackupCode(value) ? null : 'Code backup invalide (format: XXXX-XXXX-XXXX-XXXX)';
  },

  name: (value: string): string | null => {
    if (!value) return null;
    return isValidName(value) ? null : 'Nom invalide (minimum 2 lettres)';
  },

  frenchPhone: (value: string): string | null => {
    if (!value) return null;
    return isValidFrenchPhone(value) ? null : 'Numéro de téléphone invalide';
  },
};

/**
 * Valide un objet avec des règles de validation
 */
export function validateForm<T extends Record<string, unknown>>(
  values: T,
  rules: Partial<Record<keyof T, ((value: unknown) => string | null)[]>>
): Record<keyof T, string> {
  const errors: Partial<Record<keyof T, string>> = {};

  for (const field in rules) {
    const fieldRules = rules[field];
    if (!fieldRules) continue;

    for (const rule of fieldRules) {
      const error = rule(values[field]);
      if (error) {
        errors[field] = error;
        break; // Première erreur seulement
      }
    }
  }

  return errors as Record<keyof T, string>;
}
